// =====================================================================
// src/login.ts
//
// Lógica del login (entry point del index.html).
//
// Flow:
//   1. Esperar hidratación inicial (waitForAuthInit).
//   2. Si ya hay sesión válida → redirect a dashboard inmediatamente.
//   3. Si hay ?error= en URL → mostrar banner correspondiente.
//   4. Bind del form: validar cliente → signIn → redirect.
//
// Imports:
//   - core/auth: signIn, waitForAuthInit
//   - core/store: sesionStore
//   - core/ui/toast: toastError
//
// Autor: Simplificalo Consulting LLC
// =====================================================================

import { signIn, waitForAuthInit } from '@/core/auth';
import { sesionStore } from '@/core/store';
import { toastError } from '@/core/ui/toast';

// ─── DOM REFERENCES ──────────────────────────────────────────────────

const form = document.querySelector<HTMLFormElement>('#login-form');
const emailInput = document.querySelector<HTMLInputElement>('#login-email');
const passwordInput = document.querySelector<HTMLInputElement>('#login-password');
const submitButton = document.querySelector<HTMLButtonElement>('#login-submit');
const banner = document.querySelector<HTMLDivElement>('#login-banner');
const emailHelper = document.querySelector<HTMLSpanElement>('#login-email-helper');
const passwordHelper = document.querySelector<HTMLSpanElement>('#login-password-helper');

// ─── HELPERS DE UI ───────────────────────────────────────────────────

const setHelperError = (helper: HTMLSpanElement | null, message: string | null): void => {
  if (helper === null) return;
  if (message === null || message.length === 0) {
    helper.hidden = true;
    helper.textContent = '';
    helper.classList.remove('rb-helper--error');
  } else {
    helper.hidden = false;
    helper.textContent = message;
    helper.classList.add('rb-helper--error');
  }
};

const setInputError = (input: HTMLInputElement | null, hasError: boolean): void => {
  if (input === null) return;
  if (hasError) {
    input.classList.add('rb-input--error');
    input.setAttribute('aria-invalid', 'true');
  } else {
    input.classList.remove('rb-input--error');
    input.removeAttribute('aria-invalid');
  }
};

const setBanner = (message: string | null, type: 'error' | 'info' = 'error'): void => {
  if (banner === null) return;
  if (message === null || message.length === 0) {
    banner.hidden = true;
    banner.textContent = '';
    banner.classList.remove('rb-login__banner--error', 'rb-login__banner--info');
  } else {
    banner.hidden = false;
    banner.textContent = message;
    banner.classList.remove('rb-login__banner--error', 'rb-login__banner--info');
    banner.classList.add(`rb-login__banner--${type}`);
  }
};

const setLoading = (loading: boolean): void => {
  if (submitButton !== null) {
    if (loading) {
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.disabled = true;
    } else {
      submitButton.removeAttribute('aria-busy');
      submitButton.disabled = false;
    }
  }
  if (emailInput !== null) emailInput.disabled = loading;
  if (passwordInput !== null) passwordInput.disabled = loading;
};

// ─── VALIDACIÓN CLIENTE ──────────────────────────────────────────────

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD_LENGTH = 6;

interface ValidationResult {
  readonly valid: boolean;
  readonly emailError: string | null;
  readonly passwordError: string | null;
}

const validateForm = (email: string, password: string): ValidationResult => {
  let emailError: string | null = null;
  let passwordError: string | null = null;

  if (email.length === 0) {
    emailError = 'Ingresa tu email.';
  } else if (!EMAIL_REGEX.test(email)) {
    emailError = 'Email inválido.';
  }

  if (password.length === 0) {
    passwordError = 'Ingresa tu contraseña.';
  } else if (password.length < MIN_PASSWORD_LENGTH) {
    passwordError = `Mínimo ${String(MIN_PASSWORD_LENGTH)} caracteres.`;
  }

  return {
    valid: emailError === null && passwordError === null,
    emailError,
    passwordError,
  };
};

// ─── HANDLERS ────────────────────────────────────────────────────────

const handleSubmit = async (event: SubmitEvent): Promise<void> => {
  event.preventDefault();
  setBanner(null);

  if (emailInput === null || passwordInput === null) return;

  const email = emailInput.value.trim();
  const password = passwordInput.value;

  // Validación cliente.
  const validation = validateForm(email, password);
  setInputError(emailInput, validation.emailError !== null);
  setInputError(passwordInput, validation.passwordError !== null);
  setHelperError(emailHelper, validation.emailError);
  setHelperError(passwordHelper, validation.passwordError);

  if (!validation.valid) {
    return;
  }

  // Llamar a signIn.
  setLoading(true);
  const result = await signIn(email, password);
  setLoading(false);

  if (!result.ok) {
    // Mostrar error en banner para credenciales/account issues,
    // toast para errores de red.
    if (result.code === 'network_error') {
      toastError(result.message);
    } else {
      setBanner(result.message, 'error');
    }
    return;
  }

  // Login exitoso → redirect a dashboard.
  window.location.href = '/dashboard.html';
};

const handleInputChange = (
  input: HTMLInputElement | null,
  helper: HTMLSpanElement | null,
): void => {
  if (input === null) return;
  if (input.classList.contains('rb-input--error')) {
    setInputError(input, false);
    setHelperError(helper, null);
  }
};

// ─── BANNER POR QUERY PARAMS ─────────────────────────────────────────

const BANNER_MESSAGES_BY_ERROR_CODE: Record<string, string> = {
  unauthorized: 'No tienes permisos para acceder a esta página.',
  session_expired: 'Tu sesión expiró. Inicia sesión de nuevo.',
};

const checkUrlError = (): void => {
  const params = new URLSearchParams(window.location.search);
  const errorCode = params.get('error');
  if (errorCode === null) return;

  const message = BANNER_MESSAGES_BY_ERROR_CODE[errorCode];
  if (message !== undefined) {
    setBanner(message, 'error');
  }

  // Limpiar query string para que reload no re-muestre el banner.
  const cleanUrl = window.location.pathname;
  window.history.replaceState({}, '', cleanUrl);
};

// ─── BOOTSTRAP ───────────────────────────────────────────────────────

const bootstrap = async (): Promise<void> => {
  // Esperar hidratación de auth (lee sesión persistida si la hay).
  await waitForAuthInit();

  // Si ya hay sesión válida, redirect inmediato.
  const sesion = sesionStore.get();
  if (sesion?.activo === true) {
    window.location.href = '/dashboard.html';
    return;
  }

  // Si no hay sesión, mostrar el form normalmente.
  checkUrlError();

  // Bind del form.
  if (form !== null) {
    form.addEventListener('submit', (e) => {
      void handleSubmit(e);
    });
  }

  // Limpiar errores al re-tipear.
  if (emailInput !== null) {
    emailInput.addEventListener('input', () => {
      handleInputChange(emailInput, emailHelper);
    });
  }
  if (passwordInput !== null) {
    passwordInput.addEventListener('input', () => {
      handleInputChange(passwordInput, passwordHelper);
    });
  }
};

void bootstrap();
